import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import Row from 'reactstrap/lib/Row';
import Col from 'reactstrap/lib/Col';
import dateFormat from 'dateformat';
import { connect } from 'react-redux';
import { compact, isUndefined } from 'lodash';
import { VehicleEntities } from 'client/data/models/vehicle-v2';
import { ConsumerReviewsEntities } from 'client/data/models/consumer-reviews';
import { SCROLL_DURATION, SCROLL_OFFSET } from 'site-modules/shared/constants/sub-navigation';
import { TIME } from 'client/utils/time';
import { adjustTimezone, createDate } from 'site-modules/shared/utils/date-utils';
import { buildUpdateReviewUrl } from 'site-modules/shared/utils/consumer-reviews/url-builder';
import { ScrollElement } from 'site-modules/shared/components/scroll-link/scroll-element';
import { TruncatedText } from 'site-modules/shared/components/truncated-text/truncated-text';
import { ClientSideRender } from 'site-modules/shared/components/client-side-render/client-side-render';
import { Link } from 'site-modules/shared/components/link/link';
import { FormattedReviewTextByNLPInference } from 'site-modules/shared/components/reviews/formatted-review-text-by-nlp-inference';
import { RatingStars } from 'site-modules/shared/components/rating-stars/rating-stars';
import { HelpfulQuestion } from './helpful-question';

import './review-item.scss';

const RATINGS = [
  { key: 'safety', name: 'Safety' },
  { key: 'technology', name: 'Technology' },
  { key: 'performance', name: 'Performance' },
  { key: 'interior', name: 'Interior' },
  { key: 'comfort', name: 'Comfort' },
  { key: 'reliability', name: 'Reliability' },
  { key: 'value', name: 'Value' },
];
const MAX_TEXT_LENGTH = {
  true: 475,
  false: 3900,
};

function ReviewItemUI({ vehicle, isMobile, review, dataTrackingParent }) {
  const [isHelpful, setIsHelpful] = useState(undefined);

  if (!review) {
    return null;
  }

  const {
    make: { name: makeName, slug: makeSlug },
    model: { name: modelName, slug: modelSlug },
    year,
  } = vehicle;
  const {
    id,
    text,
    title,
    author,
    created,
    updated,
    styleName,
    thumbsUp,
    thumbsDown,
    vehicleRating,
    spans,
    year: reviewYear,
    city,
    state,
  } = review;
  const { overall } = vehicleRating;
  const categorized = vehicleRating.categorized || {};

  // graphQL return omits thumbsUpDownCounter
  const thumbsCounter = { thumbsUp, thumbsDown };

  const updatedThumbsUpDown = {
    thumbsUp: thumbsCounter.thumbsUp + (!isUndefined(isHelpful) && isHelpful),
    thumbsDown: thumbsCounter.thumbsDown + (!isUndefined(isHelpful) && !isHelpful),
  };

  const headingId = `heading-${id}`;
  const descriptionId = `helpful-text-${id}`;
  const scrollTop = `scrollTop-${id}`;

  const hasInferences = spans && spans.length;
  const showUpdatedString = !!updated && createDate(updated) - createDate(created) > TIME.MS.ONE_MONTH;

  const reportLink = `https://help.edmunds.com/hc/en-us/requests/new?ticket_form_id=2343&ticket[subject]=Report%20review%20(${id})%20to%20Edmunds.com%20for%20investigation&ticket[description]=I%20am%20reporting%20this%20review%20%22${title}%22%20posted%20by%20${
    author.authorName
  }%20because`;

  const starsSection = (
    <Row key={`stars-section-${id}`} className="mt-1_5 medium">
      <Col xs={12} md={8}>
        <Row className="justify-content-between flex-column flex-md-row">
          {RATINGS.map(({ key, name }) => {
            const rating = categorized[key];

            if (rating > 0) {
              const domKey = `${id}-${name}-${rating}`;
              return (
                <Col
                  tag="dl"
                  key={domKey}
                  xs={7}
                  sm={4}
                  md={5}
                  className="mb-1 d-flex justify-content-between pe-1_5 pe-sm-0 pe-md-1_5 pe-lg-0 pe-xl-2_5"
                >
                  <dt className="fw-normal">{name}</dt>
                  <dd className="mb-0">
                    <RatingStars rating={rating} className="text-primary-darker" />
                  </dd>
                </Col>
              );
            }

            return '';
          })}
        </Row>
      </Col>
    </Row>
  );

  return (
    <div
      className="review-item text-gray-darker"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
      data-tracking-parent={dataTrackingParent}
    >
      <RatingStars rating={overall} className="text-primary-darker me-0_25" />
      <h2 id={headingId} className="heading-5 d-inline mb-0">
        <Link to={buildUpdateReviewUrl({ make: makeSlug, model: modelSlug, year, id })} rel="nofollow">
          {title}
        </Link>
      </h2>

      <div className="small text-gray-dark mb-2">
        <div>
          {compact([
            author.authorName,
            city,
            state,
            dateFormat(adjustTimezone(createDate(created)), 'mm/dd/yyyy'),
          ]).join(', ')}{' '}
          {showUpdatedString && (
            <div className="d-inline-block px-0_25 text-white bg-primary-darker rounded">
              updated {dateFormat(adjustTimezone(createDate(updated)), 'mm/dd/yyyy')}
            </div>
          )}
        </div>
        <div>
          {reviewYear} {makeName} {modelName} {styleName}
        </div>
      </div>

      <div id={descriptionId} className="xsmall mb-1_5">
        {updatedThumbsUpDown.thumbsUp} of {updatedThumbsUpDown.thumbsUp + updatedThumbsUpDown.thumbsDown} people found
        this review helpful
      </div>

      <ScrollElement id={scrollTop} />
      <ClientSideRender
        placeholder={
          <Fragment>
            <p>{text}</p>
            {starsSection}
          </Fragment>
        }
      >
        {text &&
          (hasInferences ? (
            <Fragment>
              <FormattedReviewTextByNLPInference review={review} isMobile={isMobile} key={id} />
              {starsSection}
            </Fragment>
          ) : (
            <TruncatedText
              text={text}
              isInlineBtn
              btnClassName="fw-normal heading-5 text-primary-darker mb-0"
              btnTextMore="Read more"
              btnTextLess="Read less"
              indicatorOpenClass="icon-arrow-down4 text-primary-darker xsmall ps-0_5"
              indicatorCloseClass="icon-arrow-up4 text-primary-darker xsmall ps-0_5"
              maxTextLength={MAX_TEXT_LENGTH[isMobile]}
              className="size-16"
              alwaysShowAdditionalContent={!isMobile}
              additionalContent={starsSection}
              resetParams={{
                offset: SCROLL_OFFSET,
                duration: SCROLL_DURATION,
                to: scrollTop,
              }}
              expandTrackingId="crr_navigate_expand"
              collapseTrackingId="crr_navigate_collapse"
            />
          ))}

        {!!id && (
          <HelpfulQuestion reviewId={id} thumbsUpDownCounter={updatedThumbsUpDown} setIsHelpful={setIsHelpful} />
        )}
      </ClientSideRender>

      <a className="report-link xsmall text-gray-dark" href={reportLink} rel="nofollow">
        Report Abuse
      </a>

      <hr className="mt-1_5" />
    </div>
  );
}

ReviewItemUI.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear.isRequired,
  review: ConsumerReviewsEntities.ConsumerReview,
  dataTrackingParent: PropTypes.string,
  isMobile: PropTypes.bool,
};

ReviewItemUI.defaultProps = {
  review: null,
  dataTrackingParent: null,
  isMobile: false,
};

export const ReviewItem = connect(({ mobile }) => ({
  isMobile: mobile,
}))(ReviewItemUI);
